const dataCentersFrontRangeSpotlightData = {
    displayName: 'data centers',
    // Manually update when the spotlight's content changes.
    publishedOn: 'July 2026',
    hero: {
        image: require('../../../assets/img/spotlights/dataCenter.jpg'),
        title: 'Data Centers & Colorado’s Front Range',
        description:
            'Colorado’s Front Range is in the middle of a data center building boom, ' +
            'driven largely by the surge in AI compute demand. This Spotlight explores what ' +
            'that boom costs in land, energy, and water, who lives with those costs, and how ' +
            'Colorado’s expansion compares to the rest of the country. From Weld County to ' +
            'Aurora to Colorado Springs, we map where facilities are being built, what they ' +
            'consume, and what the surrounding communities look like.',
        tagline: 'Data centers are not neutral infrastructure.',
    },

    sections: {
        dataSources: {
            title: 'Explore Key Data Sources',
            description:
                'Discover the datasets behind Colorado’s data center footprint: ' +
                'facility locations, land parcels, energy demand, water use, and Census ' +
                'demographics for surrounding communities.',
        },
        dataProducts: {
            title: 'Explore Data Center Maps and Reports',
            description:
                'Interactive maps, legislative reports, and visualizations that bring ' +
                'Colorado’s data center story to life.',
        },
        starterPrompts: {
            title: 'Don’t Know Where to Start? Ask Bili!',
            description:
                'Here are some ways to explore Colorado’s data center story:',
        },
    },

    whyItMatters: {
        title: 'Why Data Centers Matter on Colorado’s Front Range',
        intro:
            'Data centers are among the most resource-intensive land uses in the ' +
            'built environment, drawing significant electricity, consuming water for ' +
            'cooling, and occupying large parcels of land. They don’t land randomly. ' +
            'Understanding where they’re being built, what they consume, and who lives ' +
            'nearby matters for communities, planners, and policymakers navigating this ' +
            'transformation.',
        keyStats: [
            {
                value: '80+',
                label: 'operational or planned data centers tallied statewide as of mid-2026, most clustered along the Front Range',
                source: {
                    name: 'Baxtel data center marketplace tally',
                    url: 'https://baxtel.com/data-center/colorado-co',
                },
            },
            {
                value: '177',
                unit: 'MW',
                label:
                    'drawn by the QTS Aurora data center alone, about as much power as the entire ' +
                    'city of Loveland needs (171 to 174 MW to supply roughly 35,000 homes and ' +
                    'businesses, a city of more than 80,000 people)',
                source: {
                    name: 'Vail Daily (Xcel Energy PUC filing)',
                    url: 'https://www.vaildaily.com/news/best-data-centers-in-colorado-yes-but-on-what-terms/',
                },
            },
            {
                value: '$22',
                unit: 'billion',
                label: 'that Xcel Energy says it needs to spend by 2040 to add 12 to 14 gigawatts of new generation and transmission capacity to meet projected Colorado data center demand',
                source: {
                    name: 'Colorado Sun',
                    url: 'https://coloradosun.com/2025/08/18/colorado-xcel-data-center-demand-spending/',
                },
            },
            {
                value: '66',
                unit: 'billion liters',
                label: 'of water consumed by U.S. data centers in 2023, up from 21.2 billion in 2014, equivalent to over 500,000 acre-feet per year.',
                source: {
                    name: 'DOE / LBNL 2024 Data Center Energy Usage Report',
                    url: 'https://eta-publications.lbl.gov/sites/default/files/2024-12/lbnl-2024-united-states-data-center-energy-usage-report_1.pdf',
                },
            },
        ],
        narrative: [
            {
                heading: 'The Build-Out: A Front Range Transformation',
                text:
                    'Colorado already has more than 60 data centers, most clustered along ' +
                    'the Front Range, with dozens more planned or under construction, some ' +
                    'exceeding 160 megawatts of capacity apiece. A single facility can already ' +
                    'rival a small city: the QTS Aurora data center alone draws 177 MW, about ' +
                    'as much power as the entire city of Loveland needs to supply roughly ' +
                    '35,000 homes and businesses. The single largest planned project, the ' +
                    'Cielo Hudson Data Center, would add 750 MW on its own. In Weld County, ' +
                    'Global AI purchased a roughly 500-acre parcel (the former Carestream ' +
                    'Health/Kodak site) for $15.6 million to build a large-scale AI facility. East of Denver, Port ' +
                    'Colorado offers a 6,500-plus-acre footprint with access to strong ' +
                    'electrical grids and major fiber lines. In Bennett along the I-70 ' +
                    'corridor, the 102-acre Muegge Farms parcel is being actively marketed to ' +
                    'developers for its available substation access. In Colorado Springs, the ' +
                    'Raeden-backed Project Taurus is retrofitting an existing tech campus at ' +
                    '1565 High Tech Way near Garden of the Gods Road; it has been approved by ' +
                    'the City Planning Department, though the zoning modifications drew ' +
                    'community pushback. Even rural communities are drawn in: BluSky AI ' +
                    'acquired 36 acres in Walsenburg for a modular AI facility.',
                sources: [
                    {
                        name: 'Colorado Newsline',
                        url: 'https://coloradonewsline.com/2025/12/12/what-data-centers-really-mean-colorados/',
                    },
                    {
                        name: 'Vail Daily',
                        url: 'https://www.vaildaily.com/news/best-data-centers-in-colorado-yes-but-on-what-terms/',
                    },
                    {
                        name: 'Colorado Springs Gazette',
                        url: 'https://gazette.com/2026/03/28/ai-data-center-plans-baffle-small-southern-colorado-town/',
                    },
                    {
                        name: 'Colorado Legislative Council Staff – Data Center Impacts Report',
                        url: 'https://content.leg.colorado.gov/sites/default/files/r26-49-data-center-impacts-on-economy-and-the-environment-accessible.pdf',
                    },
                ],
            },
            {
                heading: 'How Colorado Compares Nationally',
                text:
                    'Colorado is a mid-tier player in a rapidly expanding national market. ' +
                    'Virginia leads all states with more than 650 operational data centers; ' +
                    'Texas follows with over 400. Colorado’s current footprint is smaller, ' +
                    'but it’s experiencing rapid acceleration. A 2025 PwC ' +
                    'study cited by industry advocates described Colorado as an “outlier” that ' +
                    'was “falling behind” despite its strong technology workforce, available ' +
                    'power, and available land. That framing has animated a legislative debate ' +
                    'about tax incentives that, as of mid-2026, remains unresolved: Colorado is ' +
                    'one of only about 13 states that does not offer significant incentives to ' +
                    'attract data center investment. At the same time, communities near ' +
                    'established data center hubs elsewhere are increasingly pushing ' +
                    'development toward secondary markets like Colorado. As one analysis put ' +
                    'it, the data center balloon is being squeezed: restrict it in one place ' +
                    'and it bulges out in another.',
                sources: [
                    {
                        name: 'Visual Capitalist – Ranked: Which U.S. States Will Be Data Center Hotspots?',
                        url: 'https://www.visualcapitalist.com/ranked-us-states-data-center-hotspots/',
                    },
                    {
                        name: 'Visual Capitalist – Mapped: U.S. States With the Most Data Centers in 2025',
                        url: 'https://www.visualcapitalist.com/mapped-u-s-states-with-the-most-data-centers-in-2025/',
                    },
                    {
                        name: 'Vail Daily',
                        url: 'https://www.vaildaily.com/news/best-data-centers-in-colorado-yes-but-on-what-terms/',
                    },
                    {
                        name: 'Boulder Daily',
                        url: 'https://boulderdaily.net/2026/05/25/data-center-regulations-elude-colorado-lawmakers-again-as-state-grapples-with-booming-industry/',
                    },
                    {
                        name: 'Brownstein Hyatt – Colorado’s Data Center Dilemma: Carrot or Stick?',
                        url: 'https://www.bhfs.com/insight/colorados-data-center-dilemma-carrot-or-stick/',
                    },
                ],
            },
            {
                heading: 'The Resource Draw: Energy and Water',
                text:
                    'No public, facility-level reporting requirement exists for data ' +
                    'center electricity or water consumption in Colorado or at the federal ' +
                    'level, so the figures below are estimates derived from utility filings ' +
                    'and industry benchmarks rather than direct facility measurements. With that ' +
                    'caveat: data centers are among the most energy- and water-intensive ' +
                    'land uses in the built environment. Nationally, U.S. data center electricity use ' +
                    'grew from 1.9 to 4.4 percent of total U.S. electricity between 2018 and ' +
                    '2023, and is projected to reach 6.7 to 12 percent by 2028. In Colorado, Xcel Energy ' +
                    'projects Colorado demand growth accelerating five to six times its ' +
                    'prior rate, with data centers driving roughly two-thirds of new load. ' +
                    'Water use follows a similar path: U.S. data centers consumed 66 ' +
                    'billion liters in 2023, more than triple the levels in 2014.',
                sources: [
                    {
                        name: 'Colorado Legislative Council Staff – Data Center Impacts on the Environment, Public Health, and Energy Costs',
                        url: 'https://content.leg.colorado.gov/sites/default/files/r26-49-data-center-impacts-on-economy-and-the-environment-accessible.pdf',
                    },
                    {
                        name: 'LBNL 2024 Data Center Energy Usage Report',
                        url: 'https://eta-publications.lbl.gov/sites/default/files/2024-12/lbnl-2024-united-states-data-center-energy-usage-report_1.pdf',
                    },
                    {
                        name: 'Vail Daily (Xcel Energy PUC filing)',
                        url: 'https://www.vaildaily.com/news/best-data-centers-in-colorado-yes-but-on-what-terms/',
                    },
                    {
                        name: 'OECD.AI – How much water does AI consume?',
                        url: 'https://oecd.ai/en/wonk/how-much-water-does-ai-consume',
                    },
                ],
            },
            {
                heading: 'The Data We Don’t Have',
                text:
                    'The estimates above are the best available, but they’re still estimates, ' +
                    'not facility-level measurements. That gap has real consequences: city ' +
                    'councils, county commissioners, and the Colorado Public Utilities ' +
                    'Commission are approving or blocking specific data center projects without ' +
                    'facility-level consumption data to evaluate, and the communities living ' +
                    'next to these facilities have no independent way to verify an operator’s ' +
                    'claims about how much energy or water it actually uses.',
                groups: [
                    {
                        label: 'What data exists, and its limits:',
                        bullets: [
                            'U.S. Department of Energy/Lawrence Berkeley National Laboratory ' +
                                'reporting provides national aggregate estimates, not figures for ' +
                                'any individual facility',
                            'Utility filings provide demand forecasts for planning purposes, ' +
                                'not actual usage data',
                            'Facility trackers disagree even on how many data centers exist: ' +
                                'as of mid-2026, Baxtel tallies 80+ operational or planned ' +
                                'facilities statewide, Data Center Map lists 57 facilities from ' +
                                '50 operators, and Cleanview counts just 13 operating plus 6 ' +
                                'planned. None of the three include resource usage data, and none ' +
                                'of them agree on locations or counts',
                            'Power Usage Effectiveness (PUE) is the industry’s most common ' +
                                'energy metric and is sometimes self-reported, but it only measures ' +
                                'the ratio of total facility energy to IT-equipment energy. It ' +
                                'doesn’t capture absolute electricity demand, the carbon intensity ' +
                                'of that electricity, computational efficiency, or geographic ' +
                                'differences in cooling need, so a facility’s environmental ' +
                                'footprint can grow even as its PUE improves',
                            'Water Usage Effectiveness (WUE) and total water usage figures lag ' +
                                'energy metrics badly: they’re far less standardized, ' +
                                'facility-level disclosure is uncommon, and without geographic ' +
                                'context even a reported figure says little about a facility’s ' +
                                'exposure to local water stress or permitting risk',
                        ],
                    },
                    {
                        label: 'What’s missing outright:',
                        bullets: [
                            'Facility-level energy consumption tied to a specific address',
                            'Facility-level water withdrawals, discharge, and consumption',
                            'The carbon intensity of the electricity each facility actually ' +
                                'draws',
                            'Noise data',
                        ],
                    },
                    {
                        label: 'Why we don’t have it:',
                        bullets: [
                            'Companies use non-disclosure agreements to protect what they ' +
                                'consider trade secrets',
                            'No federal or statewide registry of data centers exists',
                            'Some jurisdictions have no legal definition of a “data center” or ' +
                                '“high-usage facility,” so there’s nothing on the books to require ' +
                                'reporting from in the first place',
                            'Utility companies generally aren’t required to disclose how much ' +
                                'energy or water an individual customer uses',
                        ],
                    },
                    {
                        label: 'Transparency efforts underway:',
                        bullets: [
                            'Colorado’s SB26-102 (Large-Load Data Centers) would have required ' +
                                'data centers over 30 MW to report annual water and energy use to ' +
                                'CDPHE starting in 2028; it died in committee on May 11, 2026, four ' +
                                'days after companion bill HB26-1030 also failed',
                            'At the federal level, the Data Center Water and Energy Disclosure ' +
                                'Act (introduced by Sen. Durbin) and the Data Center Transparency ' +
                                'Act (H.R. 6984) both propose facility-level reporting requirements ' +
                                'and remain pending in Congress',
                        ],
                    },
                ],
                sources: [
                    {
                        name: 'Open Praxis – The Black Box of Accountability: The Transparency Problem',
                        url: 'https://openpraxis.org/articles/10.55982/openpraxis.17.4.1058#the-black-box-of-accountability-the-transparency-problem',
                    },
                    {
                        name: 'Tech Policy Press – The State of Data Center Policy in the United States',
                        url: 'https://www.techpolicy.press/the-state-of-data-center-policy-in-the-united-states/',
                    },
                    {
                        name: 'OECD – Measuring the Environmental Impacts of Artificial Intelligence: Compute and Applications',
                        url: 'https://www.oecd.org/en/publications/measuring-the-environmental-impacts-of-artificial-intelligence-compute-and-applications_7babf571-en.html',
                    },
                    {
                        name: 'Environmental Defense Fund – Data Center Energy and Water Metrics',
                        url: 'https://library.edf.org/AssetLink/6v1aqy6p160n5y45q7ld1p2um2l2812q.pdf',
                    },
                    {
                        name: 'Data Center Map – Colorado Data Centers',
                        url: 'https://www.datacentermap.com/usa/colorado/',
                    },
                    {
                        name: 'Cleanview – Colorado Data Center Map',
                        url: 'https://cleanview.co/data-centers/colorado',
                    },
                    {
                        name: 'Colorado General Assembly – SB26-102 Large-Load Data Centers',
                        url: 'https://leg.colorado.gov/bills/SB26-102',
                    },
                    {
                        name: 'Colorado General Assembly – HB26-1030 Data Center & Utility Modernization',
                        url: 'https://leg.colorado.gov/bills/hb26-1030',
                    },
                    {
                        name: 'Sen. Durbin – Data Center Water and Energy Disclosure Act',
                        // eslint-disable-next-line max-len
                        url: 'https://www.durbin.senate.gov/newsroom/press-releases/as-utility-costs-rise-durbin-introduces-new-legislation-to-bring-transparency-to-energy-and-water-consumption-by-data-centers',
                    },
                    {
                        name: 'Congress.gov – H.R. 6984, Data Center Transparency Act',
                        url: 'https://www.congress.gov/bill/119th-congress/house-bill/6984/text',
                    },
                ],
            },
            {
                heading: 'The Regulatory Response: Moratoria and Restrictions',
                text:
                    'The pace of development has prompted direct regulatory action from ' +
                    'several Front Range jurisdictions:',
                bullets: [
                    'Denver: On May 20, 2026, the City Council voted unanimously for a ' +
                        'one-year moratorium on new data center construction and rezoning, ' +
                        'driven largely by opposition to CoreSite’s development in the ' +
                        'Globeville-Elyria-Swansea neighborhood. It doesn’t affect facilities ' +
                        'already operating or permitted, and gives the city time to develop ' +
                        'zoning, building, and energy code requirements addressing noise, air ' +
                        'pollution, and utility impacts.',
                    'Longmont: Banned new hyperscale data centers consuming 100 MW or more ' +
                        '(or five percent or more of regional grid capacity) to prioritize ' +
                        'local utility capacity for smaller-scale biotech and innovation uses.',
                    'Jefferson County: The day after Denver’s vote, commissioners enacted a ' +
                        '10-month moratorium halting all new data center zoning and ' +
                        'development approvals for projects not already underway, directing ' +
                        'officials to evaluate impacts on the environment, water supplies, ' +
                        'and the Wildland-Urban Interface.',
                    'Larimer County: Established a temporary moratorium on new land use ' +
                        'applications in unincorporated areas in January 2026, later extended ' +
                        'to August 25, 2026, to give the county time to draft Land Use Code ' +
                        'definitions and standards addressing water consumption, power usage, ' +
                        'noise, and aesthetics.',
                    'Logan County: Enacted a six-month moratorium in October 2025 and has ' +
                        'since passed comprehensive regulations (Resolution 2026-5).',
                    'Weld County: Approved a new data center ordinance (Code Ordinance ' +
                        '2026-01) in April 2026.',
                ],
                sources: [
                    {
                        name: 'Colorado Sun',
                        url: 'https://coloradosun.com/2026/06/18/colorado-data-center-update-pollution-protests-moratoriums/',
                    },
                    {
                        name: 'Spencer Fane – Data Centers in Colorado: Local Governments Take the Lead in Regulating Siting',
                        url: 'https://www.spencerfane.com/insight/data-centers-in-colorado-local-governments-take-the-lead-in-regulating-siting/',
                    },
                    {
                        name: 'Rocky Mountain Real Estate Law – Colorado’s Unresolved Data Center Debate',
                        url: 'https://www.rockymountainrealestatelaw.com/2026/05/colorados-unresolved-data-center-debate/',
                    },
                    {
                        name: 'Larimer County – Temporary Moratorium on Data Centers',
                        url: 'https://www.larimer.gov/planning/temp-moratorium-data-centers',
                    },
                ],
            },
        ],
        learnMore: [
            {
                name: 'Colorado Legislative Council Staff – Data Center Impacts Report',
                url: 'https://content.leg.colorado.gov/sites/default/files/r26-49-data-center-impacts-on-economy-and-the-environment-accessible.pdf',
                description:
                    'State-level analysis of data center energy demand, water use, public health, and economic impacts in Colorado',
            },
            {
                name: 'Grid Strategies – National Load Growth Report 2025',
                url: 'https://gridstrategiesllc.com/wp-content/uploads/Grid-Strategies-National-Load-Growth-Report-2025.pdf',
                description:
                    'National context for data center-driven electricity demand growth and power demand forecasts',
            },
            {
                name: 'Colorado Public Utilities Commission',
                url: 'https://puc.colorado.gov',
                description:
                    'Large customer interconnection requests and utility filings related to data center load in Colorado',
            },
            {
                name: 'EPA ECHO',
                url: 'https://echo.epa.gov',
                description:
                    'Facility-level environmental discharge data for industrial water use',
            },
            {
                name: 'Colorado Division of Water Resources',
                url: 'https://dwr.colorado.gov',
                description:
                    'Industrial water rights and permit records for Front Range facilities',
            },
            {
                name: 'U.S. Census Bureau',
                url: 'https://data.census.gov',
                description:
                    'American Community Survey tract-level demographic data for communities surrounding major data center facilities',
            },
            {
                name: 'CDPHE Industrial Discharge Permits',
                url: 'https://cdphe.colorado.gov/water-quality-permits',
                description:
                    'Water withdrawal and discharge data tied to individual facility permits',
            },
        ],
    },

    communityPicture: {
        title: 'The Community Picture: Who Lives Nearby',
        intro:
            'Data centers don’t land randomly, and the communities that live alongside ' +
            'them deserve to know who their neighbors are. Below, we compare U.S. Census ' +
            'Bureau American Community Survey data for the census tract containing each of ' +
            'four Front Range facilities against that tract’s county average: median ' +
            'household income, housing tenure, and racial and ethnic makeup. The intent of ' +
            'this comparison is to provide context, not to assert that any site was chosen ' +
            'because of who lives nearby. The underlying siting decisions involve land ' +
            'cost, power and fiber access, and zoning that this Spotlight does not attempt ' +
            'to adjudicate.',
        methodologyNote:
            'Every figure below is a 5-year estimate (2020–2024) and carries its own ' +
            'published margin of error (±, 90% confidence), shown next to each number. ' +
            'Estimates for small census tracts carry wider margins of error than county ' +
            'figures, so tract-level numbers are directional context, not precise ' +
            'measurements. Percentages shown in the bars are derived from the underlying ' +
            'counts for visual comparison; the counts and their margins of error are the ' +
            'sourced figures. One facility (Project Taurus) was geocoded to its street, not ' +
            'an exact building, so its tract match, while highly likely, is not confirmed ' +
            'against a rooftop coordinate.',
        source: {
            name: 'U.S. Census Bureau – ACS 5-Year Estimates (2020–2024)',
            url: 'https://data.census.gov',
        },
        facilities: [
            {
                name: 'CoreSite DE3',
                address: '4900 Race Street, Denver, CO',
                county: 'Denver County',
                tractLabel: 'Census Tract 35.01',
                medianIncome: {
                    tract: { value: 67386, moe: 22631 },
                    county: { value: 94718, moe: 1644 },
                },
                tenure: {
                    tract: {
                        total: { value: 1073, moe: 138 },
                        owner: { value: 433, moe: 103, pct: 40.4 },
                        renter: { value: 640, moe: 140, pct: 59.6 },
                    },
                    county: {
                        total: { value: 335428, moe: 1490 },
                        owner: { value: 163555, moe: 2609, pct: 48.8 },
                        renter: { value: 171873, moe: 2647, pct: 51.2 },
                    },
                },
                raceEthnicity: {
                    tract: {
                        total: { value: 3416, moe: 427 },
                        white: { value: 410, moe: 157, pct: 12.0 },
                        black: { value: 285, moe: 205, pct: 8.3 },
                        asian: { value: 0, moe: 14, pct: 0.0 },
                        hispanic: { value: 2674, moe: 462, pct: 78.3 },
                        other: { value: 47, moe: 680, pct: 1.4 },
                    },
                    county: {
                        total: { value: 718877, moe: 0 },
                        white: { value: 388268, moe: 1574, pct: 54.0 },
                        black: { value: 61862, moe: 1495, pct: 8.6 },
                        asian: { value: 25884, moe: 968, pct: 3.6 },
                        hispanic: { value: 201140, moe: 0, pct: 28.0 },
                        other: { value: 41723, moe: 2377, pct: 5.8 },
                    },
                },
            },
            {
                name: 'QTS Aurora',
                address: '1160 N Gun Club Rd, Aurora, CO',
                county: 'Arapahoe County',
                tractLabel: 'Census Tract 71.10',
                medianIncome: {
                    tract: { value: 143483, moe: 14558 },
                    county: { value: 101087, moe: 1532 },
                },
                tenure: {
                    tract: {
                        total: { value: 2663, moe: 311 },
                        owner: { value: 2568, moe: 314, pct: 96.4 },
                        renter: { value: 95, moe: 61, pct: 3.6 },
                    },
                    county: {
                        total: { value: 257232, moe: 1096 },
                        owner: { value: 166534, moe: 2086, pct: 64.8 },
                        renter: { value: 90698, moe: 1957, pct: 35.3 },
                    },
                },
                raceEthnicity: {
                    tract: {
                        total: { value: 7394, moe: 738 },
                        white: { value: 3437, moe: 597, pct: 46.5 },
                        black: { value: 1207, moe: 410, pct: 16.3 },
                        asian: { value: 512, moe: 403, pct: 6.9 },
                        hispanic: { value: 1666, moe: 453, pct: 22.5 },
                        other: { value: 572, moe: 1199, pct: 7.7 },
                    },
                    county: {
                        total: { value: 659844, moe: 0 },
                        white: { value: 364028, moe: 1092, pct: 55.2 },
                        black: { value: 69137, moe: 1714, pct: 10.5 },
                        asian: { value: 39886, moe: 1093, pct: 6.1 },
                        hispanic: { value: 143439, moe: 0, pct: 21.7 },
                        other: { value: 43354, moe: 2308, pct: 6.6 },
                    },
                },
            },
            {
                name: 'Project Taurus (Raeden)',
                address: '1565 High Tech Way, Colorado Springs, CO',
                county: 'El Paso County',
                tractLabel: 'Census Tract 79.00',
                medianIncome: {
                    tract: { value: 104552, moe: 26099 },
                    county: { value: 90363, moe: 1401 },
                },
                tenure: {
                    tract: {
                        total: { value: 1461, moe: 171 },
                        owner: { value: 1237, moe: 177, pct: 84.7 },
                        renter: { value: 224, moe: 112, pct: 15.3 },
                    },
                    county: {
                        total: { value: 290190, moe: 1184 },
                        owner: { value: 192232, moe: 1990, pct: 66.2 },
                        renter: { value: 97958, moe: 1866, pct: 33.8 },
                    },
                },
                raceEthnicity: {
                    tract: {
                        total: { value: 2661, moe: 373 },
                        white: { value: 2247, moe: 391, pct: 84.4 },
                        black: { value: 80, moe: 105, pct: 3.0 },
                        asian: { value: 75, moe: 63, pct: 2.8 },
                        hispanic: { value: 125, moe: 81, pct: 4.7 },
                        other: { value: 134, moe: 560, pct: 5.0 },
                    },
                    county: {
                        total: { value: 742999, moe: 0 },
                        white: { value: 487079, moe: 1424, pct: 65.6 },
                        black: { value: 40648, moe: 1377, pct: 5.5 },
                        asian: { value: 20790, moe: 915, pct: 2.8 },
                        hispanic: { value: 139197, moe: 0, pct: 18.7 },
                        other: { value: 55285, moe: 2182, pct: 7.4 },
                    },
                },
            },
            {
                name: 'Flexential Parker',
                address: '15255 Compark Blvd, Parker, CO',
                county: 'Douglas County',
                tractLabel: 'Census Tract 140.14',
                medianIncome: {
                    tract: { value: 107382, moe: 18457 },
                    county: { value: 149594, moe: 2519 },
                },
                tenure: {
                    tract: {
                        total: { value: 2272, moe: 225 },
                        owner: { value: 1272, moe: 211, pct: 56.0 },
                        renter: { value: 1000, moe: 214, pct: 44.0 },
                    },
                    county: {
                        total: { value: 139960, moe: 642 },
                        owner: { value: 108309, moe: 1145, pct: 77.4 },
                        renter: { value: 31651, moe: 1168, pct: 22.6 },
                    },
                },
                raceEthnicity: {
                    tract: {
                        total: { value: 5762, moe: 657 },
                        white: { value: 3892, moe: 684, pct: 67.6 },
                        black: { value: 128, moe: 125, pct: 2.2 },
                        asian: { value: 962, moe: 342, pct: 16.7 },
                        hispanic: { value: 449, moe: 223, pct: 7.8 },
                        other: { value: 331, moe: 1040, pct: 5.7 },
                    },
                    county: {
                        total: { value: 377150, moe: 0 },
                        white: { value: 290577, moe: 985, pct: 77.1 },
                        black: { value: 5037, moe: 513, pct: 1.3 },
                        asian: { value: 21780, moe: 759, pct: 5.8 },
                        hispanic: { value: 38394, moe: 0, pct: 10.2 },
                        other: { value: 21362, moe: 1345, pct: 5.7 },
                    },
                },
            },
        ],
    },

    communitySlug: 'data-centers-colorado',

    dataSources: [],

    dataProducts: [],

    starterPrompts: [
        'Where are data centers being built on Colorado’s Front Range?',
        'How much electricity do Colorado’s data centers consume?',
        'How much water do data centers use for cooling, and how does that compare to household water use?',
        'What do the communities near major Colorado data centers look like demographically?',
        'Which Colorado cities or counties have enacted data center moratoria or restrictions?',
        'How does Colorado’s data center growth compare to states like Virginia and Texas?',
        'Why doesn’t Colorado offer tax incentives for data center development the way other states do?',
        'What land parcels are being used for data center development in Weld County and Aurora?',
        'How does household income near the Flexential Parker or QTS Aurora data centers compare to their county’s median?',
    ],
};

export default dataCentersFrontRangeSpotlightData;
